import {type ConnectionOperation, DOCUMENT_FIELDS_PATH} from '@github-ui/optimistic-connection'
import {
  createConnectionMutationId,
  dispatchConnectionMutation,
} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  ThreadSubscriptionFormAction,
  updateIssueSubscriptionMutation,
  updateIssueSubscriptionMutation$data,
  UpdateSubscriptionInput,
} from './__generated__/updateIssueSubscriptionMutation.graphql'

function toSubscriptionOperation(data: updateIssueSubscriptionMutation$data): ConnectionOperation | undefined {
  const issue = data.updateSubscription?.subscribable
  const issueId = issue?.id
  if (!issue || !issueId) return undefined
  return {
    type: 'setFields',
    id: issueId,
    fields: {
      viewerThreadSubscriptionFormAction: issue.viewerThreadSubscriptionFormAction,
      viewerCustomSubscriptionEvents: issue.viewerCustomSubscriptionEvents,
    },
  }
}

export function commitUpdateIssueSubscriptionMutation({
  environment,
  input,
  onCompleted,
  onError,
}: {
  environment: Environment
  input: UpdateSubscriptionInput
  onCompleted?: () => void
  onError?: (error: Error) => void
}) {
  const viewerThreadSubscriptionFormAction: ThreadSubscriptionFormAction =
    input.state === 'SUBSCRIBED' || input.state === 'CUSTOM' ? 'UNSUBSCRIBE' : 'SUBSCRIBE'

  const optimisticResponse = {
    updateSubscription: {
      subscribable: {
        __typename: 'Issue',
        id: input.subscribableId,
        viewerThreadSubscriptionFormAction,
        viewerCustomSubscriptionEvents: input.events,
      },
    },
  }

  const mutationId = createConnectionMutationId()
  const optimisticOperation = toSubscriptionOperation(optimisticResponse)

  // Published before `commitMutation`: a same-tick terminal would find nothing pending to settle.
  if (optimisticOperation) {
    dispatchConnectionMutation({
      phase: 'optimistic',
      mutationId,
      ownerId: input.subscribableId,
      connectionPath: DOCUMENT_FIELDS_PATH,
      operation: optimisticOperation,
    })
  }

  let disposable
  try {
    disposable = commitMutation<updateIssueSubscriptionMutation>(environment, {
      // Important: the name of this mutation is being used in app/platform/authorization/permission_check.rb (def emu_ownership_enforceable)
      // to decide whether EMU can subscribe to an issue in a public repo. If changing it, please change it there as well.
      // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
      mutation: graphql`
        mutation updateIssueSubscriptionMutation($input: UpdateSubscriptionInput!) @raw_response_type {
          updateSubscription(input: $input) {
            subscribable {
              ... on Issue {
                id
                viewerThreadSubscriptionFormAction
                viewerCustomSubscriptionEvents
              }
            }
          }
        }
      `,
      variables: {
        input,
      },
      optimisticResponse,
      onCompleted: (response, errors) => {
        const operation = toSubscriptionOperation(response)
        if (!operation) {
          // The overlay rolls back, so reporting success would close the settings dialog and
          // announce a change the UI is simultaneously reverting.
          const error = new Error(errors?.[0]?.message ?? 'Failed to update issue subscription')
          dispatchConnectionMutation({phase: 'error', mutationId, error})
          onError?.(error)
          return
        }
        dispatchConnectionMutation({
          phase: 'completed',
          mutationId,
          ownerId: input.subscribableId,
          connectionPath: DOCUMENT_FIELDS_PATH,
          operation,
        })
        onCompleted?.()
      },
      onError: error => {
        dispatchConnectionMutation({phase: 'error', mutationId, error})
        onError?.(error)
      },
      onUnsubscribe: () => {
        dispatchConnectionMutation({
          phase: 'error',
          mutationId,
          error: new Error('Updating the issue subscription was cancelled'),
        })
      },
    })
  } catch (error) {
    dispatchConnectionMutation({
      phase: 'error',
      mutationId,
      error: error instanceof Error ? error : new Error('Failed to update issue subscription'),
    })
    throw error
  }

  return disposable
}
